
import { useState, useEffect } from 'react';
import { Brain, Terminal, Zap } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { PageLayout } from '@/components/layout/PageLayout';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { WeeklyChallenge } from '@/components/practice/WeeklyChallenge';
import { DailyChallenges } from '@/components/practice/DailyChallenges';
import { PracticeTabs } from '@/components/practice/PracticeTabs';
import { ChatMessageData } from '@/components/practice/PromptChat';
import { useToast } from '@/hooks/use-toast';

const Practice = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [userInitials, setUserInitials] = useState('');
  const [challengeProgress, setChallengeProgress] = useState(1);
  const [activeDailyChallenge, setActiveDailyChallenge] = useState(1); // Default to Prompt Engineering
  const [chatMessages, setChatMessages] = useState<ChatMessageData[]>([
    {
      role: 'assistant',
      content: "Today's challenge: Use AI to create a concise analysis of your company's competitive landscape. What prompt would you use to get the most accurate and useful information?",
      timestamp: new Date().toISOString()
    }
  ]);
  
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single();
        
        if (error) {
          console.error('Error fetching user profile:', error);
          return;
        }
        
        if (data) {
          const firstInitial = data.first_name ? data.first_name.charAt(0).toUpperCase() : '';
          const lastInitial = data.last_name ? data.last_name.charAt(0).toUpperCase() : '';
          setUserInitials(firstInitial + lastInitial);
        }
      } catch (error) {
        console.error('Error in fetchUserProfile:', error);
      }
    };
    
    fetchUserProfile();
  }, [user]);

  const handleNewChallenge = () => {
    setIsLoading(true);
    
    // Different challenges based on the active challenge type
    let challenges: string[] = [];
    
    if (activeDailyChallenge === 1) {
      // Prompt Engineering challenges
      challenges = [
        "Use AI to identify potential risks in a new product launch strategy. Craft a prompt that will help uncover blind spots.",
        "Create a prompt to analyze customer feedback data and extract actionable insights for your product team.",
        "Craft a prompt to help generate a comprehensive SWOT analysis for entering a new market segment.",
        "Design a prompt that will help create personalized email templates for different customer segments.",
        "Develop a prompt that will assist in creating a realistic timeline for implementing a new AI solution in your organization."
      ];
    } else if (activeDailyChallenge === 2) {
      // We're not handling this here as the ToolComparisonChallenge has its own new challenge handler
      setIsLoading(false);
      return;
    } else if (activeDailyChallenge === 3) {
      // We're not handling this here as the UseCaseIdentificationChallenge has its own new challenge handler
      setIsLoading(false);
      return;
    }
    
    const randomChallenge = challenges[Math.floor(Math.random() * challenges.length)];
    
    const newChallengeMessage: ChatMessageData = {
      role: 'assistant',
      content: `New challenge: ${randomChallenge}`,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages([newChallengeMessage]);
    setIsLoading(false);
  };

  const handleProgressUpdate = (newProgress: number) => {
    setChallengeProgress(newProgress);
  };
  
  const handleSelectChallenge = (challengeId: number) => {
    // Ensure the challenge can be activated
    if (challengeId === activeDailyChallenge) {
      // Already active, do nothing
      return;
    }
    
    setActiveDailyChallenge(challengeId);
    
    // Update the dailyChallenges array to set the selected challenge as active
    const updatedChallenges = dailyChallenges.map(challenge => ({
      ...challenge,
      isActive: challenge.id === challengeId
    }));
    
    setDailyChallenges(updatedChallenges);
    
    const challengeName = updatedChallenges.find(c => c.id === challengeId)?.title || "";
    
    toast({
      title: `${challengeName} Activated`,
      description: "Challenge is now ready in the practice area.",
    });
  };

  const [dailyChallenges, setDailyChallenges] = useState([
    {
      id: 1,
      title: "Prompt Engineering",
      description: "Create effective prompts for specific business outcomes",
      isActive: true,
      isCompleted: false,
      icon: <Terminal className="h-6 w-6" />,
    },
    {
      id: 2,
      title: "AI Tool Comparison",
      description: "Compare different AI tools for a specific use case",
      isActive: false,
      isCompleted: false,
      icon: <Zap className="h-6 w-6" />,
    },
    {
      id: 3,
      title: "Use Case Identification",
      description: "Identify AI opportunities in your organization",
      isActive: false,
      isCompleted: false,
      icon: <Brain className="h-6 w-6" />,
    }
  ]);
  
  const weeklyChallenge = {
    title: "Strategic AI Implementation",
    description: "Draft a brief plan for introducing an AI tool to your team",
    daysLeft: 3,
    progress: challengeProgress,
    totalSteps: 4
  };

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Practice Lab</h1>
          <p className="text-muted-foreground">Apply your AI knowledge with hands-on challenges</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            {/* Swapped order: Daily Challenges now appear first */}
            <DailyChallenges 
              challenges={dailyChallenges} 
              onSelectChallenge={handleSelectChallenge}
            />
            <WeeklyChallenge 
              {...weeklyChallenge} 
              onProgressUpdate={handleProgressUpdate}
            />
          </div>

          <div className="lg:col-span-2">
            <Card className="glass-card h-full flex flex-col">
              <div className="p-6 flex-grow flex flex-col">
                <PracticeTabs
                  chatMessages={chatMessages}
                  setChatMessages={setChatMessages}
                  userInitials={userInitials}
                  handleNewChallenge={handleNewChallenge}
                  isLoading={isLoading}
                  activeDailyChallenge={activeDailyChallenge}
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Practice;
